import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'CareerCard', level: 'info' });

interface SchemaField {
  type: string;
  label?: string;
  order?: number;
  fields?: string[];
  [key: string]: unknown;
}

interface CardConfig {
  [key: string]: SchemaField;
}

interface CareerCardSchema {
  id: string;
  card_type: string;
  display_name: string;
  version: number;
  schema: CardConfig;
  is_active: boolean;
}

interface CareerCardContextType {
  schemas: CareerCardSchema[];
  latestSchema: CareerCardSchema | null;
  isLoading: boolean;
  error: Error | null;
  schemaTypes: string[];
  schemasLoaded: number;
  forceRefresh: () => Promise<void>;
}

const CareerCardContext = createContext<CareerCardContextType | undefined>(undefined);

export const CareerCardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [schemas, setSchemas] = useState<CareerCardSchema[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [schemaTypes, setSchemaTypes] = useState<string[]>([]);
  const [schemasLoaded, setSchemasLoaded] = useState<number>(0);
  const initRef = useRef(false);

  const loadSchemas = async () => {
    logger.debug('Starting schema load');
    setIsLoading(true);
    try {
      const { data: schemas, error } = await supabase
        .from('career_card_schemas')
        .select('*')
        .eq('is_active', true)
        .order('card_type', { ascending: true });

      if (error) throw error;

      setSchemas(schemas || []);
      setSchemaTypes(schemas?.map(s => s.card_type) || []);
      setSchemasLoaded(schemas?.length || 0);
      setIsLoading(false);
    } catch (error) {
      logger.error('Error loading schemas', error);
      setError(error as Error);
      setIsLoading(false);
    }
  };

  const forceRefresh = async () => {
    initRef.current = true;
    await loadSchemas();
  };

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    loadSchemas();
  }, []);

  useEffect(() => {
    if (!initRef.current) return;
    
    logger.render({ 
      schemasLoaded: schemaTypes.length, 
      isLoading, 
      error, 
      schemaTypes 
    });
  }, [schemaTypes.length, isLoading, error]);

  return (
    <CareerCardContext.Provider
      value={{
        schemas,
        latestSchema: null,
        isLoading,
        error,
        schemaTypes,
        schemasLoaded,
        forceRefresh
      }}
    >
      {children}
    </CareerCardContext.Provider>
  );
};

export const useCareerCard = (): CareerCardContextType => {
  const context = useContext(CareerCardContext);
  if (context === undefined) {
    throw new Error('useCareerCard must be used within a CareerCardProvider');
  }
  return context;
};